import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  updateProfile,
  updatePassword,
  sendEmailVerification,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { auth, db, googleProvider } from "../firebase/config";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Handle Google redirect result on page load
    getRedirectResult(auth).catch(() => {});

    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        try {
          const snap = await getDoc(doc(db, "users", fbUser.uid));
          if (snap.exists()) {
            setUser({ uid: fbUser.uid, email: fbUser.email, ...snap.data() });
          } else {
            const newUser = {
              email: fbUser.email,
              displayName: fbUser.displayName || fbUser.email.split("@")[0],
              photoURL: fbUser.photoURL || "",
              plan: "free",
              isAdmin: false,
              createdAt: new Date().toISOString(),
              totalDocumentsScanned: 0,
              totalWordsProcessed: 0,
              dailyScansUsed: 0,
              lastScanDate: new Date().toISOString(),
            };
            await setDoc(doc(db, "users", fbUser.uid), newUser);
            setUser({ uid: fbUser.uid, ...newUser });
          }
        } catch (err) {
          // Firestore rules are blocking client reads — use Firebase Auth data as fallback.
          // Fix: Firebase Console → Firestore → Rules → allow read/write for users/{uid}.
          console.error("Firestore permission denied — check security rules:", err.message);
          setUser({
            uid: fbUser.uid,
            email: fbUser.email,
            displayName: fbUser.displayName || fbUser.email?.split("@")[0] || "",
            photoURL: fbUser.photoURL || "",
            plan: "free",
            isAdmin: false,
          });
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const login = async (email, password) => {
    const { user: fbUser } = await signInWithEmailAndPassword(auth, email, password);
    return fbUser;
  };

  const register = async (email, password, displayName) => {
    const { user: fbUser } = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) await updateProfile(fbUser, { displayName });
    const newUser = {
      email,
      displayName: displayName || email.split("@")[0],
      photoURL: "",
      plan: "free",
      isAdmin: false,
      createdAt: new Date().toISOString(),
      totalDocumentsScanned: 0,
      totalWordsProcessed: 0,
      dailyScansUsed: 0,
      lastScanDate: new Date().toISOString(),
      emailVerified: false,
    };
    await setDoc(doc(db, "users", fbUser.uid), newUser);
    // Send email verification
    try { await sendEmailVerification(fbUser); } catch {}
    // Sync with backend: triggers welcome email + isAdmin sync
    try { await api.get("/auth/me"); } catch {}
    return fbUser;
  };

  const resendVerification = async () => {
    if (auth.currentUser) {
      await sendEmailVerification(auth.currentUser);
    }
  };

  const googleLogin = async () => {
    try {
      return await signInWithPopup(auth, googleProvider);
    } catch (err) {
      if (err.code === "auth/popup-blocked" || err.code === "auth/cancelled-popup-request") {
        return signInWithRedirect(auth, googleProvider);
      }
      throw err;
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  const refresh = async () => {
    if (!auth.currentUser) return;
    const snap = await getDoc(doc(db, "users", auth.currentUser.uid));
    if (snap.exists()) {
      setUser({ uid: auth.currentUser.uid, email: auth.currentUser.email, ...snap.data() });
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    const fbUser = auth.currentUser;
    const credential = EmailAuthProvider.credential(fbUser.email, currentPassword);
    await reauthenticateWithCredential(fbUser, credential);
    await updatePassword(fbUser, newPassword);
  };

  const updateDisplayName = async (displayName) => {
    if (!auth.currentUser) return;
    await updateProfile(auth.currentUser, { displayName });
    await updateDoc(doc(db, "users", auth.currentUser.uid), { displayName });
    setUser((u) => ({ ...u, displayName }));
  };

  const emailVerified = auth.currentUser?.emailVerified ?? true;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleLogin, logout, refresh, changePassword, updateDisplayName, setUser, resendVerification, emailVerified }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
